/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { useState, Fragment, useMemo, useEffect, ReactNode, memo } from 'react';
import { CustomPlacement, Popup } from '../../components/Popup';
import { useSlot } from '../../hooks/useSlot';
import { EventSlot } from './EventSlot';
import { ExternalWeekly } from './EventSlot/ExternalWeekly';
import { InternalWeekly } from './EventSlot/InternalWeekly';
import { Maintenance, MaintenanceWeekly } from './MaintenanceSlot';
import { SlotPopupBody } from './SlotPopupBody';
import { ColorCode, IExpandedSlot, IPopupActions, IPopupRedirections } from './types';
import { ResourceNameTypeEnum } from '../../types/enums';
import { isDailyMode } from '../../calendar/utils/dnd';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

enum SlotTypeEnum {
	EXTERNAL = 'external',
	INTERNAL = 'internal',
	MAINTENANCE = 'maintenance',
	CUSTOM = 'custom',
}

export enum ECalendarMode {
	DAILY = 'daily',
	WEEKLY = 'weekly',
	MONTHLY = 'monthly',
	DAYX = 'dayx',
}

export type SlotPopupProps = {
	Component: ReactNode;
	toggle: () => void;
	isOpen: boolean;
};

type SlotProps = {
	event: any;
	triggerEvent: (v: any) => void;
	mode: ECalendarMode;
	isDragging?: boolean;
	isConflict?: boolean;
	conflictCount?: number;
	activeId?: number;
	fetchExpandModeSlot?: (slotId: number, reservationId: number, slotType: string) => any;
	Popup?: SlotPopupProps;
	onContextMenu?: any;
	colorCode?: ColorCode;
	isSlotEditable?: boolean;
	currentDate?: string;
	horizontal?: boolean;
	redirections?: IPopupRedirections;
	actions?: IPopupActions;
};

const slotTriggerCss = (horizontal?: boolean) => css`
	display: ${horizontal ? 'flex' : 'block'};
	${horizontal ? 'flex-direction: row;' : ''}
	height: 100%;
	width: 100%;
`;

const LARGE_SLOT_IN_HOURS = 20;

export const Slot = (props: SlotProps) => {
	const [isShowing, setShowing] = useState<boolean>(false);
	const [expandedSlot, setExpandedSlot] = useState<IExpandedSlot>();
	const { isMaintenance, isDaily, isInternal } = useMemo(() => {
		const { event, mode } = props;
		return {
			isMaintenance: event.slotType === SlotTypeEnum.MAINTENANCE,
			isDaily: isDailyMode(mode),
			isInternal: event?.slotType === SlotTypeEnum.INTERNAL,
		};
	}, [props]);

	const isLargeSlot: boolean = useMemo(() => {
		const { event } = props;

		const start: dayjs.Dayjs = dayjs(`${event.startDate}T${event.startTime}`);
		const end: dayjs.Dayjs = dayjs(`${event.endDate}T${event.endTime}`);
		return end.diff(start, 'hour') >= LARGE_SLOT_IN_HOURS;
	}, [props.event]);

	const customPlacement: CustomPlacement = useMemo(() => {
		if (isLargeSlot) {
			const centerX: number = Math.round(window.innerWidth / 2);
			const centerY: number = Math.round(window.innerHeight / 2);
			return { left: centerX, top: centerY };
		}

		return { top: 0, left: 0 };
	}, [isLargeSlot]);

	const { isEditable, startTime, endTime, isDraft } = useSlot({ state: props.event });

	const actions: IPopupActions = useMemo(() => {
		if (!props.actions) {
			return {};
		}

		const { edit, delete: deleteActions, duplicate, editNotes, viewForms, charge } = props.actions;
		const cardChargeLabel = expandedSlot ? charge?.getLabel?.(expandedSlot) : undefined;
		const chargeLabel = charge?.getLabel ? cardChargeLabel : charge?.label;

		return {
			edit: {
				allow: Boolean(edit?.allow),
				handle:
					edit?.allow && isEditable && expandedSlot
						? () => {
								toggle();
								edit?.handle?.({ ...props.event, startTime, endTime });
							}
						: undefined,
			},
			duplicate: {
				allow: Boolean(duplicate?.allow),
				handle:
					duplicate?.allow && expandedSlot
						? () => {
								toggle();
								duplicate?.handle?.({ ...props.event, startTime, endTime });
							}
						: undefined,
			},
			delete: {
				allow: Boolean(deleteActions?.allow),
				handle:
					deleteActions?.allow && expandedSlot
						? () => {
								toggle();
								deleteActions?.handle?.(expandedSlot);
							}
						: undefined,
			},
			editNotes: {
				allow: Boolean(editNotes?.allow),
				handle: editNotes?.allow
					? () => {
							toggle();
							editNotes?.handle?.(expandedSlot);
						}
					: undefined,
			},
			viewForms: {
				allow: Boolean(viewForms?.allow) && Boolean(expandedSlot?.hasApplications),
				handle: Boolean(viewForms?.allow) ? () => {
					toggle();
					viewForms?.handle?.(expandedSlot);
				} : undefined,
			},
			charge: {
				allow: Boolean(charge?.allow) && Boolean(chargeLabel),
				label: chargeLabel,
				handle:
					charge?.allow && expandedSlot
						? () => {
								toggle();
								charge?.handle?.(expandedSlot);
							}
						: undefined,
			},
		};
	}, [props.actions, isEditable, startTime, endTime, props.event, expandedSlot]);

	useEffect(() => {
		if (isShowing) {
			if (!props.fetchExpandModeSlot) {
				return;
			}

			props
				.fetchExpandModeSlot(
					props.event.id,
					props.event.reservation?.id ?? props.event.reservationId,
					props.event.slotType
				)
				.then((data: IExpandedSlot) => setExpandedSlot({ ...data, conflictCount: props.conflictCount ?? 0 }));
		}
	}, [isShowing]);

	const toggle = () => setShowing(isShowing => !isShowing);

	const isProgram = props?.event?.event?.parentType === ResourceNameTypeEnum.PROGRAM_SEASON;

	if (isDraft) {
		return (
			<span
				style={{ cursor: 'default' }}
				key={`Draft-SlotView-${props.event.id}`}
				data-aid="slot-wrapper"
				data-slot-root
				css={slotTriggerCss(props.horizontal)}
			>
				<SlotView isMaintenance={isMaintenance} isDaily={isDaily} isInternal={isInternal} {...props} />
			</span>
		);
	}

	if (isProgram) {
		return (
			<div
				style={{ height: '100%', width: '100%' }}
				key={`Program-SlotView-${props.event.id}`}
				data-aid="slot-wrapper"
				data-slot-root
			>
				<Fragment key={`Program-SlotView-${props.event.id}`}>
					<span
						onClick={props.Popup?.toggle}
						onContextMenu={props.onContextMenu}
						css={slotTriggerCss(props.horizontal)}
					>
						<SlotView isMaintenance={isMaintenance} isDaily={isDaily} isInternal={isInternal} {...props} />
					</span>
					{props.Popup?.isOpen && props.Popup?.Component}
				</Fragment>
			</div>
		);
	}

	return (
		<div
			style={{ height: '100%', width: '100%' }}
			key={`Rental-SlotView-${props.event.id}`}
			data-aid="slot-wrapper"
			data-slot-root
		>
			<Popup
				disableMaxHeight
				disableAutoTrigger
				disableScroll={true}
				trigger={isShowing}
				onOpenChange={setShowing}
				padding={0}
				customPlacement={customPlacement}
				limitPlacements={false}
				key={`Rental-SlotPopup-${props.event.id}`}
				layerWidth={'100%'}
				layerMaxWidth={'52rem'}
				body={
					<SlotPopupBody
						toggle={toggle}
						expandedSlot={expandedSlot}
						redirections={props.redirections}
						actions={actions}
					/>
				}
			>
				<span onClick={toggle} onContextMenu={props.onContextMenu} css={slotTriggerCss(props.horizontal)}>
					<SlotView isMaintenance={isMaintenance} isDaily={isDaily} isInternal={isInternal} {...props} />
				</span>
			</Popup>
		</div>
	);
};

type PropsView = {
	isMaintenance: boolean;
	isDaily: boolean;
	isInternal: boolean;
	horizontal?: boolean;
} & SlotProps;

const SlotView = ({ isMaintenance, isDaily, isInternal, ...props }: PropsView) => {
	if (isMaintenance) {
		return isDaily ? (
			<Maintenance
				slot={props.event}
				isDragging={props.isDragging}
				isConflict={props.isConflict}
				colorCode={props.colorCode}
				horizontal={props.horizontal}
			/>
		) : (
			<MaintenanceWeekly state={props.event} isConflict={props.isConflict} colorCode={props.colorCode} />
		);
	}
	if (isInternal) {
		return isDaily ? (
			<EventSlot isInternal={true} {...props} />
		) : (
			<InternalWeekly state={props.event} isConflict={props.isConflict} colorCode={props.colorCode} />
		);
	}

	return isDaily ? <EventSlot isInternal={false} {...props} /> : <ExternalWeekly {...props} />;
};
